/**
 * CR462: Image Capture Debug Modal
 *
 * Admin-only modal showing:
 *   - Job status summary counts
 *   - Stuck jobs list with age, stage, worker
 *   - Per-job trace event timeline
 *   - Admin actions: timeout stale, requeue failed, cancel batch
 */

import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertCircle,
  Archive,
  Bug,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Info,
  Loader2,
  RefreshCw,
  RotateCcw,
  Server,
  XCircle,
  Zap,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow, format } from "date-fns";
import { cn } from "@/lib/utils";

// ─── Types ───────────────────────────────────────────────────────────────────

interface JobSummary {
  status: string;
  count: number;
}

interface StuckJob {
  id: string;
  batch_run_id: string | null;
  capture_attempt_id: string | null;
  merchant_source_id: string | null;
  merchant_id: string | null;
  adapter_key: string | null;
  status: string;
  stage: string | null;
  worker_id: string | null;
  run_token: string | null;
  attempt_no: number;
  started_at: string | null;
  heartbeat_at: string | null;
  last_trace_at: string | null;
  error_message: string | null;
  error_code: string | null;
  age_seconds: number | null;
  stale_seconds: number | null;
}

interface TraceEvent {
  event_id: string;
  job_id: string;
  created_at: string;
  level: string;
  event: string;
  stage: string | null;
  message: string | null;
  data_json: Record<string, unknown> | null;
  adapter_key: string | null;
  merchant_id: string | null;
  worker_id: string | null;
  attempt_no: number | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchRunId: string;
}

// ─── Level config ────────────────────────────────────────────────────────────

const LEVEL_CONFIG: Record<string, { cls: string; icon: React.ReactNode }> = {
  INFO: { cls: "text-muted-foreground", icon: <Clock className="h-3 w-3" /> },
  WARN: { cls: "text-warning", icon: <AlertCircle className="h-3 w-3" /> },
  ERROR: { cls: "text-destructive", icon: <XCircle className="h-3 w-3" /> },
};

const STATUS_BADGE: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  QUEUED: "secondary",
  RUNNING: "default",
  COMPLETE: "outline",
  FAILED: "destructive",
  TIMED_OUT: "destructive",
  CANCELLED: "secondary",
};

function fmtAge(seconds: number | null): string {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

// ─── Summary bar ─────────────────────────────────────────────────────────────

function SummaryBar({ summaries }: { summaries: JobSummary[] }) {
  const total = summaries.reduce((sum, s) => sum + s.count, 0);
  if (total === 0) return (
    <p className="text-xs text-muted-foreground italic">No jobs for this batch run.</p>
  );
  return (
    <div className="flex flex-wrap gap-2">
      {summaries.map((s) => (
        <div key={s.status} className="flex items-center gap-1.5 bg-muted/40 rounded px-2 py-1">
          <Badge variant={STATUS_BADGE[s.status] ?? "secondary"} className="text-xs h-4 px-1">
            {s.status}
          </Badge>
          <span className="text-sm font-semibold">{s.count}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Timeline panel ──────────────────────────────────────────────────────────

function TimelinePanel({ jobId }: { jobId: string }) {
  const [events, setEvents] = useState<TraceEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data } = await (supabase as any)
        .from("v_flyer_image_capture_job_timeline")
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false })
        .limit(50);
      setEvents(data ?? []);
      setLoading(false);
    }
    load();
  }, [jobId]);

  if (loading) return (
    <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
      <Loader2 className="h-3 w-3 animate-spin" /> Loading timeline…
    </div>
  );

  if (events.length === 0) return (
    <p className="text-xs text-muted-foreground italic py-2">No trace events recorded for this job.</p>
  );

  return (
    <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
      {events.map((e) => {
        const lvl = LEVEL_CONFIG[e.level] ?? LEVEL_CONFIG.INFO;
        return (
          <div key={e.event_id} className={cn("flex items-start gap-2 text-xs", lvl.cls)}>
            <div className="mt-0.5 flex-shrink-0">{lvl.icon}</div>
            <div className="min-w-0">
              <span className="font-mono font-semibold">{e.event}</span>
              {e.stage && <span className="ml-1 opacity-70">@{e.stage}</span>}
              {e.message && <span className="ml-1 text-foreground/70">— {e.message}</span>}
              <div className="text-[10px] text-muted-foreground">
                {format(new Date(e.created_at), "HH:mm:ss")}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Stuck job row ───────────────────────────────────────────────────────────

function StuckJobRow({ job }: { job: StuckJob }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded-md overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/40 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        )}
        <span className="font-mono text-[10px] text-muted-foreground w-16 truncate">
          {job.id.slice(0, 8)}
        </span>
        <Badge variant={STATUS_BADGE[job.status] ?? "secondary"} className="text-[10px] h-4 px-1">
          {job.status}
        </Badge>
        {job.adapter_key && (
          <span className="text-muted-foreground font-medium">{job.adapter_key}</span>
        )}
        {job.stage && (
          <span className="text-warning font-semibold">@{job.stage}</span>
        )}
        <span className="ml-auto text-muted-foreground">
          age {fmtAge(job.age_seconds)} · stale {fmtAge(job.stale_seconds)}
        </span>
      </button>

      {expanded && (
        <div className="border-t bg-muted/20 px-3 py-2 space-y-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
            {job.worker_id && (
              <>
                <span className="font-medium">Worker</span>
                <span className="font-mono truncate">{job.worker_id}</span>
              </>
            )}
            {job.run_token && (
              <>
                <span className="font-medium">Token</span>
                <span className="font-mono truncate">{job.run_token}</span>
              </>
            )}
            {job.attempt_no != null && (
              <>
                <span className="font-medium">Attempt #</span>
                <span>{job.attempt_no}</span>
              </>
            )}
            {job.started_at && (
              <>
                <span className="font-medium">Started</span>
                <span>{formatDistanceToNow(new Date(job.started_at), { addSuffix: true })}</span>
              </>
            )}
            {job.heartbeat_at && (
              <>
                <span className="font-medium">Last heartbeat</span>
                <span>{formatDistanceToNow(new Date(job.heartbeat_at), { addSuffix: true })}</span>
              </>
            )}
            {job.error_code && (
              <>
                <span className="font-medium text-destructive">Error code</span>
                <span className="text-destructive font-mono">{job.error_code}</span>
              </>
            )}
            {job.error_message && (
              <>
                <span className="font-medium text-destructive">Error</span>
                <span className="text-destructive">{job.error_message}</span>
              </>
            )}
          </div>

          <div>
            <p className="text-[10px] font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
              Trace Timeline
            </p>
            <TimelinePanel jobId={job.id} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main modal ──────────────────────────────────────────────────────────────

export function ImageCaptureDebugModal({ open, onOpenChange, batchRunId }: Props) {
  const { toast } = useToast();
  const [summaries, setSummaries] = useState<JobSummary[]>([]);
  const [stuckJobs, setStuckJobs] = useState<StuckJob[]>([]);
  const [orphanCount, setOrphanCount] = useState(0);
  const [detachedLegacyCount, setDetachedLegacyCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, stuckRes, orphanRes, detachedRes] = await Promise.all([
        // Status counts for this batch
        supabase
          .from("flyer_image_capture_test_runs")
          .select("status")
          .eq("batch_run_id", batchRunId),
        // Stuck jobs — already scoped to batch_run_id IS NOT NULL in the view
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from("v_flyer_image_capture_stuck_jobs")
          .select("*")
          .eq("batch_run_id", batchRunId)
          .order("age_ms", { ascending: false })
          .limit(30),
        // Real orphans (point at missing merchant_source)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from("v_flyer_image_capture_orphan_jobs")
          .select("id", { count: "exact", head: true }),
        // Detached legacy (merchant_source_id + batch_run_id both null)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from("v_flyer_image_capture_detached_legacy_jobs")
          .select("id", { count: "exact", head: true }),
      ]);

      // Aggregate status counts
      const counts: Record<string, number> = {};
      for (const row of summaryRes.data ?? []) {
        counts[row.status] = (counts[row.status] || 0) + 1;
      }
      const ordered = ["QUEUED", "RUNNING", "COMPLETE", "FAILED", "TIMED_OUT", "CANCELLED"];
      setSummaries(
        ordered
          .filter((s) => counts[s] != null)
          .map((s) => ({ status: s, count: counts[s] }))
      );

      setStuckJobs(stuckRes.data ?? []);
      setOrphanCount(orphanRes.count ?? 0);
      setDetachedLegacyCount(detachedRes.count ?? 0);
    } finally {
      setLoading(false);
    }
  }, [batchRunId]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const runWatchdogAction = async (
    action: string,
    label: string,
    confirmMsg?: string
  ) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setActing(action);
    try {
      const res = await supabase.functions.invoke("image-capture-watchdog", {
        body: { action, batch_run_id: batchRunId },
      });
      if (res.error) throw res.error;
      const d = res.data as Record<string, unknown>;
      toast({
        title: label,
        description: `Done. ${JSON.stringify(
          Object.fromEntries(
            Object.entries(d).filter(([k]) => k !== "job_ids")
          )
        )}`,
      });
      await fetchData();
    } catch (err) {
      toast({
        title: `${label} failed`,
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setActing(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <Bug className="h-4 w-4 text-primary" />
            <DialogTitle className="text-base">Image Capture Debug</DialogTitle>
            <span className="text-xs font-mono text-muted-foreground ml-1">
              {batchRunId.slice(0, 8)}…
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7 px-2"
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Status Summary */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Job Status Summary
            </p>
            {loading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            ) : (
              <SummaryBar summaries={summaries} />
            )}
          </section>

          {/* Orphan / Legacy counts */}
          {!loading && (orphanCount > 0 || detachedLegacyCount > 0) && (
            <section className="space-y-1.5">
              {orphanCount > 0 && (
                <div className="flex items-center gap-2 rounded border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs">
                  <AlertCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
                  <span>
                    <strong className="text-destructive">Orphan Jobs (Action Required):</strong>{" "}
                    {orphanCount} job{orphanCount !== 1 ? "s" : ""} still reference a deleted merchant source.
                  </span>
                </div>
              )}
              {detachedLegacyCount > 0 && (
                <div className="flex items-center gap-2 rounded bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    <strong>Detached Legacy Jobs (Informational):</strong>{" "}
                    {detachedLegacyCount} detached legacy row{detachedLegacyCount !== 1 ? "s" : ""} — no action required. Use <em>Archive Legacy</em> to clean up.
                  </span>
                </div>
              )}
            </section>
          )}

          {/* Admin Actions */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Admin Actions
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={!!acting}
                onClick={() =>
                  runWatchdogAction(
                    "timeout_stale",
                    "Timeout Stale Jobs",
                    "Mark all stale RUNNING jobs as TIMED_OUT? This cannot be undone."
                  )
                }
              >
                {acting === "timeout_stale" ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Clock className="h-3.5 w-3.5 mr-1.5" />
                )}
                Timeout Stale
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={!!acting}
                onClick={() =>
                  runWatchdogAction(
                    "requeue_failed",
                    "Requeue Failed Jobs",
                    "Requeue all FAILED/TIMED_OUT jobs back to QUEUED?"
                  )
                }
              >
                {acting === "requeue_failed" ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                )}
                Requeue Failed
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs text-muted-foreground"
                disabled={!!acting || detachedLegacyCount === 0}
                onClick={() =>
                  runWatchdogAction(
                    "archive_detached_legacy",
                    "Archive Detached Legacy",
                    `Archive ${detachedLegacyCount} detached legacy job(s) older than 7 days? They will be marked ARCHIVED (not deleted).`
                  )
                }
              >
                {acting === "archive_detached_legacy" ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Archive className="h-3.5 w-3.5 mr-1.5" />
                )}
                Archive Legacy
              </Button>

              <Button
                variant="destructive"
                size="sm"
                className="h-8 text-xs"
                disabled={!!acting}
                onClick={() =>
                  runWatchdogAction(
                    "cancel_batch",
                    "Cancel Batch Jobs",
                    "Cancel ALL active (QUEUED + RUNNING) jobs for this batch run?"
                  )
                }
              >
                {acting === "cancel_batch" ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                )}
                Cancel All Active
              </Button>
            </div>
          </section>

          {/* Stuck Jobs */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Stuck / Stale Jobs
              </p>
              {stuckJobs.length > 0 && (
                <Badge variant="destructive" className="text-[10px] h-4 px-1.5">
                  {stuckJobs.length}
                </Badge>
              )}
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            ) : stuckJobs.length === 0 ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 rounded px-3 py-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                No stuck or stale jobs detected for this batch run.
              </div>
            ) : (
              <div className="space-y-1.5">
                {stuckJobs.map((j) => (
                  <StuckJobRow key={j.id} job={j} />
                ))}
              </div>
            )}
          </section>

          {/* Scheduler hint */}
          <section className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded px-3 py-2">
            <Server className="h-3.5 w-3.5 mt-px flex-shrink-0" />
            <span>
              The watchdog auto-runs on a schedule and marks stale jobs within 1 interval (~60s).
              Use <strong>Timeout Stale</strong> above to trigger it immediately.
            </span>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
